import {
  IGoodInventoryProductFilterBody,
  IGoodInventoryProductFilterResponseData,
} from "@/api_framework/api_modals/Inventory";
import PaginationComponent from "@/app/components/ui/Pagination";
import TableSkeleton from "@/app/components/ui/Skeletons/TableSkeleton";
import { useProductFilterAPI } from "@/app/hooks/api_hooks/Inventory/GoodInventory/useProductFilterAPI";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useModalState } from "@/app/hooks/ui_hooks/useModalState";
import { useLoader } from "@/app/hooks/useLoader";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { useEffect, useRef, useState } from "react";
import { IoEllipsisHorizontalCircleOutline } from "react-icons/io5";
import InventoryMovementFilters from "../InventoryMovementFilters";
import { useUtils } from "@/app/hooks/useUtils";
import NoRecordFoundComponent from "@/app/components/ui/NoRecordFound";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";

export interface TableItem {
  name: string;
  category: string;
  lot: string;
  manufacturer: string;
  expDate: string;
  mrp: string;
  goodQty: string;
}
const PageSize = 10;

const GoodInventoryActions = () => {
  const [activeRow, setActiveRow] = useState<number | null>(null);
  const [productFilters, setProductFilters] = useState<IGoodInventoryProductFilterBody>();
  const { execute: productFilterAPI, data } = useProductFilterAPI();
  const inventoryData: IGoodInventoryProductFilterResponseData["data"] = Array.isArray(data?.data) ? data.data : [];
  const dropdownRef = useRef(null);
  const [refresh, setRefresh] = useState(false);
  const { setVisible: goodToBad, setModalData, isVisible } = useModalActions("GOOD_TO_BAD_MODAL");
  const {
    setVisible: shrink,
    setModalData: setShrinkModalData,
    isVisible: isShrinkVisible,
  } = useModalActions("SHRINK_MODAL");
  const [_loading, setLoading] = useState(true);
  // RBAC: GOOD_INVENTORY_ROW_ACTION — legacy roles: clinic_ops, clinic_ops_doggyworld, central_ops
  const canPerformGoodInventoryAction = useFeatureAccess("GOOD_INVENTORY_ROW_ACTION");
  const modalState = useModalState("GOOD_TO_BAD_MODAL");
  const shrinkModalState = useModalState("SHRINK_MODAL");
  const isProductMoveGoodToBadInProgess = useLoader("isInventoryProductGoodInventory");
  let isClinicError = false;
  const [currentPage, setCurrentPage] = useState(1);
  const [shouldCallAPI, setShouldCallAPI] = useState(true);
  const { veticDateFormat } = useUtils();
  const { NO_PWL_RESULTS } = useImageConstants();

  useEffect(() => {
    if (shouldCallAPI && productFilters?.clinic_ids?.length > 0) {
      const filterBody = {
        ...productFilters,
        clinic_ids: productFilters?.clinic_ids,
      } as IGoodInventoryProductFilterBody;
      if (!isClinicError && !filterBody.clinic_ids) {
        isClinicError = true;
        return;
      }
      setLoading(true);
      productFilterAPI(filterBody, "good_inventory", currentPage, PageSize).finally(() => setLoading(false));
    }
  }, [shouldCallAPI, productFilters, currentPage, PageSize, refresh, isVisible(), isShrinkVisible()]);

  const handleModalUpdate = () => {
    setRefresh((prev) => !prev);
    setShouldCallAPI(true);
  };

  const handleIconClick = (rowIndex: number, goodInventoryQuantity: number, item) => {
    setShouldCallAPI(false);
    if (goodInventoryQuantity > 0) {
      setActiveRow(activeRow === rowIndex ? null : rowIndex);
      setModalData({
        ...modalState,
        status: false,
        vetic_variant_id: item.vetic_variant_id,
        unique_row_id: item?.unique_row_id,
        grn_id: item.grn_id,
        sub_grn_id: item.sub_grn_id,
        sub_category_id: item.sub_category_id,
      });
      setShrinkModalData({
        ...shrinkModalState,
        status: false,
        unique_row_id: item?.unique_row_id,
      });
    }
  };

  const handleMoveToGoodToBadClick = (item) => {
    setModalData({
      ...modalState,
      qty: item.good_inventory_quantity,
      unique_row_id: item?.unique_row_id,
      sub_category_id: item?.sub_category_id,
      product_name: item.product_name,
      lot_number: item.lot_number,
      manufacturer_name: item.manufacturer_name,
      expiry_date: item.expiry_date,
      mrp: item.mrp,
      status: false,
    });
    goodToBad();
    handleModalUpdate();
  };

  const handleMoveToShrinkClick = (item) => {
    setShouldCallAPI(false);
    setShrinkModalData({
      ...shrinkModalState,
      qty: item.good_inventory_quantity,
      unique_row_id: item?.unique_row_id,
      product_name: item.product_name,
      lot_number: item.lot_number,
      manufacturer_name: item.manufacturer_name,
      expiry_date: item.expiry_date,
      mrp: item.mrp,
    });
    shrink();
    handleModalUpdate();
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setActiveRow(null);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="rounded-md px-2 py-3">
      <h2 className="text-base font-semibold">Actions on Good Inventory</h2>
      <InventoryMovementFilters setProductFilters={setProductFilters} />
      <div>
        {isProductMoveGoodToBadInProgess ? (
          <TableSkeleton />
        ) : (
          <div>
            {inventoryData.length ? (
              <div className="mt-5 pb-5 w-full">
                <div className="w-full rounded-xl bg-[#F5F5F5] px-[3px] pb-[1px]">
                  <div className="w-full rounded-xl">
                    <div className="inline-block py-1 align-middle w-full">
                      <table className="w-full border-separate border-spacing-x-0 border-spacing-y-0.5">
                        <thead className="bg-[#F5F5F5] text-[#1F1F23] top-0 font-normal">
                          <tr className="py-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] capitalize px-3">
                            <th className="px-3 py-3 text-left whitespace-nowrap text-xs text-black font-medium">
                              Product Name
                            </th>
                            <th className="px-6 py-3 text-left whitespace-nowrap text-xs text-black font-medium">
                              Category
                            </th>
                            <th className="px-6 py-3 text-left whitespace-nowrap text-xs text-black font-medium">
                              Lot #
                            </th>
                            <th className="px-6 py-3 text-left whitespace-nowrap text-xs text-black font-medium">
                              Manufacturer
                            </th>
                            <th className="px-6 py-3 text-left whitespace-nowrap text-xs text-black font-medium">
                              Exp Date
                            </th>
                            <th className="px-6 py-3 text-left text-xs text-black font-medium">MRP (Tax Inclusive)</th>
                            <th className="px-6 py-3 text-left whitespace-nowrap text-xs text-black font-medium">
                              Good / Sellable Qty
                            </th>
                            {canPerformGoodInventoryAction && (
                                <th className="px-6 py-3 text-left whitespace-nowrap text-xs text-black font-medium">
                                  Action Menu
                                </th>
                              )}
                          </tr>
                        </thead>
                        <tbody className="overflow-hidden rounded-xl w-full">
                          {inventoryData.length > 0 ? (
                            inventoryData.map((item, index) => (
                              <tr key={index} className="group bg-white transition">
                                <td className={"px-3 py-4 text-xs font-normal text-left"}>
                                  <p>{item.product_name}</p>
                                </td>
                                <td className={"px-6 py-4 text-xs font-normal text-left"}>
                                  <p>{item.category}</p>
                                </td>
                                <td className={"px-6 py-4 text-xs font-normal text-left"}>
                                  <p>{item.lot_number}</p>
                                </td>
                                <td className={"px-6 py-4 text-xs font-normal text-left"}>
                                  <p>{item.manufacturer_name}</p>
                                </td>
                                <td className={"px-6 py-4 text-xs font-normal text-left whitespace-nowrap"}>
                                  <p>{veticDateFormat(item.expiry_date)}</p>
                                </td>
                                <td className={"px-6 py-4 text-xs font-normal text-left"}>
                                  <p>{item.mrp}</p>
                                </td>
                                <td className={"px-6 py-4 text-xs font-normal text-left"}>
                                  <p>{item.good_inventory_quantity}</p>
                                </td>
                                {canPerformGoodInventoryAction && (
                                    <td className="px-6 py-4 whitespace-nowrap text-xs font-normal text-center relative">
                                      <IoEllipsisHorizontalCircleOutline
                                        fontSize={24}
                                        color="rgb(128,129,131)"
                                        className={`cursor-pointer ${item.good_inventory_quantity === 0 ? "pointer-events-none cursor-not-allowed " : ""}`}
                                        onClick={() => handleIconClick(index, item.good_inventory_quantity, item)}
                                      />
                                      {activeRow === index && item.good_inventory_quantity > 0 && (
                                        <div
                                          ref={dropdownRef}
                                          className="bg-white shadow-md p-3 z-10 w-48 rounded-sm absolute right-0 text-left"
                                        >
                                          <p
                                            onClick={() => handleMoveToGoodToBadClick(item)}
                                            className="pt-3 cursor-pointer"
                                          >
                                            Move from good to bad
                                          </p>
                                          {item?.is_shrinkable && (
                                            <p
                                              onClick={() => handleMoveToShrinkClick(item)}
                                              className="pt-3 cursor-pointer"
                                            >
                                              Shrink
                                            </p>
                                          )}
                                        </div>
                                      )}
                                    </td>
                                  )}
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={8} className="px-3 py-4 text-center text-gray-500 text-xs">
                                No results found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <NoRecordFoundComponent
                imageURL={NO_PWL_RESULTS}
                message="No Good Inventory Data Found"
                description="No good inventory data found for the given filters, try something else"
              />
            )}
          </div>
        )}
        {productFilters?.clinic_ids && (
          <div className="flex justify-center items-center w-auto shadow-md py-2">
            <PaginationComponent
              className="pagination-bar"
              currentPage={currentPage}
              totalCount={data?.inventory_with_counts?.pagination_counts?.good_inventory_count | 0}
              pageSize={PageSize}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default GoodInventoryActions;
